import { useState, useMemo } from "react";
import { checklistProgress } from "./clientdesk";
import { PartnerForm } from "./PartnerForm";
import type { Party } from "./types";
import type { ClientTask } from "./clientdesk/operations";

// ─── helpers ────────────────────────────────────────────────────────────────
const todayStr = () => new Date().toISOString().slice(0,10);
const fmtDate  = (d:string) => { try { return new Date(d+"T00:00:00").toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"2-digit"}); } catch { return d; } };
const inr = (n:number) => "₹"+new Intl.NumberFormat("en-IN",{maximumFractionDigits:0}).format(Math.round(n));
const initials = (s:string) => s.split(" ").filter(Boolean).map(w=>w[0]).join("").slice(0,2).toUpperCase();
const uid = () => Math.random().toString(36).slice(2,8);

// ─── seed ───────────────────────────────────────────────────────────────────
const T = todayStr();
const tomorrow = new Date(Date.now()+86400000).toISOString().slice(0,10);
const in3 = new Date(Date.now()+3*86400000).toISOString().slice(0,10);
const in7 = new Date(Date.now()+7*86400000).toISOString().slice(0,10);

type SC = { id:string; name:string; type:string; status:"active"|"inactive"|"prospect"; pan?:string; gstin?:string; phone?:string; email?:string; city?:string; state?:string; pincode?:string; address?:string; fileNo:string; tags:string[]; group?:string; balance:number; balanceType:"Dr"|"Cr"; autoInvoice:boolean; portalEnabled:boolean; din?:string; tan?:string; dob?:string; contactPerson?:string; };

const CLIENTS:SC[] = [
  {id:"c1",name:"A One Toys",type:"Private Limited",status:"active",pan:"ABCDE1234Z",gstin:"29ABCDE1234Z1A",phone:"9874563210",email:"amit@aonetoys.com",city:"Bangalore",state:"Karnataka",pincode:"560010",address:"#6, Center Plaza",fileNo:"A56",tags:["Audit","GST"],group:"Murthy Family",balance:64000,balanceType:"Dr",autoInvoice:true,portalEnabled:true,contactPerson:"Amit",din:"87654321",tan:"2464567"},
  {id:"c2",name:"Numetra LTD",type:"Public Limited",status:"active",pan:"BCDEA2345F",gstin:"29BCDEA2345F1Z5",phone:"9876001234",email:"info@numetra.in",city:"Bangalore",state:"Karnataka",fileNo:"B12",tags:["GST","Audit"],balance:18000,balanceType:"Dr",autoInvoice:false,portalEnabled:true,contactPerson:"Swetha Murthy"},
  {id:"c3",name:"Mative Pvt Ltd",type:"Private Limited",status:"active",pan:"CDEAB3456G",gstin:"29CDEAB3456G1Z3",phone:"9845001122",city:"Mumbai",state:"Maharashtra",fileNo:"C22",tags:["GST"],balance:5000,balanceType:"Dr",autoInvoice:true,portalEnabled:false,contactPerson:"Raj Patel"},
  {id:"c4",name:"Vaarahi RMC",type:"Private Limited",status:"active",pan:"AABCV1234M",gstin:"29AABCV1234M1Z8",phone:"9901234567",email:"rk@vaarahi.in",city:"Bangalore",state:"Karnataka",fileNo:"V01",tags:["Audit","GST","TDS"],balance:120000,balanceType:"Dr",autoInvoice:true,portalEnabled:true,contactPerson:"Raghavendra"},
  {id:"c5",name:"Kumar Textiles",type:"Partnership",status:"active",pan:"DEABC4567H",city:"Surat",state:"Gujarat",fileNo:"K08",tags:["Audit"],balance:32000,balanceType:"Dr",autoInvoice:false,portalEnabled:false,contactPerson:"Kumar Jain"},
  {id:"c6",name:"Prestige Builders",type:"LLP",status:"active",pan:"EABCD5678I",gstin:"32EABCD5678I1Z1",city:"Kochi",state:"Kerala",fileNo:"P14",tags:["GST","TDS"],balance:8000,balanceType:"Cr",autoInvoice:true,portalEnabled:true,contactPerson:"Vikram Nair"},
];

const TASKS:ClientTask[] = [
  {id:"t1",clientId:"c1",clientName:"A One Toys",service:"GSTR-3B",returnType:"GSTR-3B",period:"Jan 2026",status:"pending",priority:"high",assignedTo:"Krithi",dueDate:T,billable:true,createdAt:"2026-01-09T00:00:00Z",checklist:[{id:"ck1",label:"Document collect",done:true},{id:"ck2",label:"Sales details",done:true},{id:"ck3",label:"Tally entries",done:true},{id:"ck4",label:"Filed",done:false}]},
  {id:"t2",clientId:"c2",clientName:"Numetra LTD",service:"Accounting",period:"Q1 FY27",status:"in-progress",priority:"normal",assignedTo:"Ayush",dueDate:T,billable:true,createdAt:"2026-01-09T00:00:00Z"},
  {id:"t3",clientId:"c3",clientName:"Mative Pvt Ltd",service:"GSTR-1",returnType:"GSTR-1",period:"Jul 2026",status:"pending",priority:"normal",assignedTo:"Krithi",dueDate:tomorrow,billable:true,createdAt:"2026-08-01T00:00:00Z"},
  {id:"t4",clientId:"c4",clientName:"Vaarahi RMC",service:"Tax Audit",period:"2023-24",status:"pending",priority:"high",assignedTo:"Krithi",dueDate:in7,billable:true,tags:["Audit"],createdAt:"2026-01-09T00:00:00Z",
    checklist:[{id:"s1",label:"Turnover Reconciliation from GSTR-1",done:true},{id:"s2",label:"Check that GSTR-1 Matches with GSTR-3B",done:true},{id:"s3",label:"GSTR-2B Reconciliation with Books",done:false},{id:"s4",label:"TDS Reconciliation as per Form 26AS",done:false},{id:"s5",label:"GST Cash Liability & Credit Ledger Reconciliation",done:false},{id:"s6",label:"TDS Deducted",done:false},{id:"s7",label:"Calculation of Income Tax Liability",done:false}]},
  {id:"t5",clientId:"c5",clientName:"Kumar Textiles",service:"Audit",period:"FY 2025-26",status:"on-hold",priority:"high",assignedTo:"Ayush",dueDate:in3,billable:true,createdAt:"2026-08-01T00:00:00Z"},
  {id:"t6",clientId:"c6",clientName:"Prestige Builders",service:"TDS",returnType:"TDS-26Q",period:"Q1 FY27",status:"completed",priority:"normal",assignedTo:"Rithika",dueDate:new Date(Date.now()-86400000).toISOString().slice(0,10),billable:true,createdAt:"2026-07-15T00:00:00Z"},
  {id:"t7",clientId:"c1",clientName:"A One Toys",service:"GSTR-1",period:"Jul 2026",status:"pending",priority:"normal",assignedTo:"Rithika",dueDate:tomorrow,billable:true,createdAt:"2026-08-01T00:00:00Z"},
  {id:"t8",clientId:"c2",clientName:"Numetra LTD",service:"Accounting",period:"Q2 FY27",status:"completed",priority:"normal",assignedTo:"Ayush",dueDate:in3,billable:true,createdAt:"2026-07-01T00:00:00Z"},
];

const HOLIDAYS = [
  {name:"Independence Day",date:"15-08-2026"},{name:"Janmashtami",date:"16-08-2026"},
  {name:"Ganesh Chaturthi",date:"27-08-2026"},{name:"Gandhi Jayanti",date:"02-10-2026"},
  {name:"Dussehra",date:"02-10-2026"},{name:"Diwali",date:"20-10-2026"},
];
const COMPLIANCE = [
  {name:"TDS",date:"07-08-2026"},{name:"GSTR-1",date:"11-08-2026"},{name:"GSTR-3B",date:"20-08-2026"},
];

// ─── Nav ────────────────────────────────────────────────────────────────────
type CDSection = "dashboard"|"tasks"|"todo"|"clients"|"packages"|"services"|"invoices"|"receipts"|"quotations"|"expenses"|"attendance"|"documents"|"reports"|"users"|"ai"|"communication"|"finance"|"employees";

const NAV_GROUPS: Array<{items:Array<{key:CDSection;label:string;icon:string;badge?:string}>}> = [
  {items:[{key:"dashboard",label:"Dashboard",icon:"⊞"},{key:"tasks",label:"Tasks",icon:"✓"},{key:"todo",label:"To-Do",icon:"📌"},{key:"clients",label:"Clients",icon:"👤"}]},
  {items:[{key:"communication",label:"Communication",icon:"💬",badge:"New"},{key:"packages",label:"Client Packages",icon:"📦"},{key:"services",label:"Services & Packages",icon:"⊙"}]},
  {items:[{key:"finance",label:"Finance",icon:"₹",badge:"New"},{key:"invoices",label:"Invoices",icon:"🧾"},{key:"receipts",label:"Receipts",icon:"🧾"},{key:"quotations",label:"Quotations",icon:"💬"},{key:"expenses",label:"Expenses",icon:"💸"}]},
  {items:[{key:"employees",label:"Employees",icon:"🧑‍💼",badge:"New"},{key:"attendance",label:"Attendance",icon:"👤"},{key:"documents",label:"Documents & DSC",icon:"📁"},{key:"reports",label:"Reports",icon:"📊"},{key:"users",label:"Users",icon:"👥"}]},
];

// ─── AI Assistant ────────────────────────────────────────────────────────────
const AI_QUICK = [
  "3 clients have overdue GSTR-3B — send WhatsApp reminders?",
  "A One Toys DSC expires in 28 days — create renewal task?",
  "Kumar Textiles audit on hold 5 days — follow up?",
  "4 invoices worth ₹2.4L are unbilled — generate now?",
];

function AIPanel({notify}:{notify:(m:string,t?:string)=>void}) {
  const [msgs,setMsgs] = useState<Array<{role:"ai"|"user";text:string}>>([{role:"ai",text:"Hi! I'm your AI practice assistant. I can draft WhatsApp reminders, generate tasks, flag compliance risks, and answer questions about your clients. What would you like to do today?"}]);
  const [inp,setInp] = useState("");
  const [loading,setLoading] = useState(false);

  const send = async (text?:string) => {
    const t = text ?? inp.trim(); if(!t) return; setInp("");
    setMsgs(p=>[...p,{role:"user" as const,text:t}]); setLoading(true);
    await new Promise(r=>setTimeout(r,900));
    const lower = t.toLowerCase();
    let reply = "Let me check your practice data...";
    if(/overdue|pending|gstr/i.test(lower)) reply = "You have 2 overdue items: A One Toys GSTR-3B (was due today) and Mative Pvt Ltd GSTR-1 (due tomorrow). Would you like me to draft WhatsApp messages for both clients to collect documents?";
    else if(/invoice|billing|unbilled/i.test(lower)) reply = "4 unbilled tasks worth approximately ₹2.4L: A One Toys (₹96,000), Vaarahi RMC (₹84,000), Kumar Textiles (₹60,000), Prestige Builders (₹40,000). Shall I generate draft invoices for all four?";
    else if(/dsc/i.test(lower)) reply = "A One Toys' DSC expires in 28 days (Class 3, token ending 234). I've checked and the renewal typically takes 3-5 business days. Want me to create a renewal task and send an auto-reminder to the client?";
    else if(/itr|income tax/i.test(lower)) reply = "Tax Audit season is underway. Vaarahi RMC's tax audit engagement is in-progress with 2/7 checklist items done. Kumar Textiles audit is on hold — waiting for physical documents. Both are assigned to Krithi. Want me to send follow-up reminders?";
    else if(/whatsapp|remind|message/i.test(lower)) reply = "Draft WhatsApp message:\n\n\"Dear [Client], Greetings from [Firm Name]. Your [GSTR-3B] for the period [Jul 2026] is due on [20-Aug-2026]. Kindly share the required documents at the earliest. For queries, contact us at [phone]. Thank you.\"\n\nShall I send this to the 3 overdue clients?";
    else if(/hi|hello|hey/i.test(lower)) reply = "Hello! Your practice today: 5 open tasks, 3 due today, ₹2.4L unbilled, 2 compliance deadlines this week. What would you like to focus on?";
    else reply = "I can help with compliance reminders, task creation, WhatsApp messages, invoice generation, and practice insights. Could you give me more details about what you need?";
    setMsgs(p=>[...p,{role:"ai" as const,text:reply}]); setLoading(false);
  };

  return (
    <div className="pv-ai-panel">
      <div className="pv-ai-panel-header">
        <div className="pv-ai-badge">✦ AI</div>
        <div><strong>Ledzerly AI Assistant</strong><small>Practice Intelligence</small></div>
        <span className="pv-ai-live-dot">● Live</span>
      </div>
      <div className="pv-ai-quick-row">
        <div className="pv-ai-quick-label">Quick actions</div>
        <div className="pv-ai-chips">
          {AI_QUICK.map((q,i)=><button key={i} className="pv-ai-chip" onClick={()=>send(q)} type="button">{q}</button>)}
        </div>
      </div>
      <div className="pv-ai-msgs">
        {msgs.map((m,i)=>(
          <div key={i} className={`pv-ai-msg ${m.role}`}>
            {m.role==="ai"&&<div className="pv-ai-msg-icon">✦</div>}
            <div className="pv-ai-bubble">{m.text}</div>
            {m.role==="user"&&<div className="pv-ai-user-dot">RK</div>}
          </div>
        ))}
        {loading&&<div className="pv-ai-msg ai"><div className="pv-ai-msg-icon">✦</div><div className="pv-ai-bubble pv-ai-typing"><span/><span/><span/></div></div>}
      </div>
      <div className="pv-ai-input-wrap">
        <input className="pv-ai-inp" value={inp} onChange={e=>setInp(e.target.value)} onKeyDown={e=>e.key==="Enter"&&send()} placeholder="Ask about clients, compliance, tasks, reminders..." />
        <button className="pv-ai-send-btn" onClick={()=>send()} type="button">→</button>
      </div>
    </div>
  );
}

// ─── Dashboard ───────────────────────────────────────────────────────────────
function Dashboard({tasks,navigate}:{tasks:ClientTask[];navigate:(s:CDSection)=>void}) {
  const dueToday = tasks.filter(t=>t.dueDate===T&&t.status!=="completed").length;
  const dueTomorrow = tasks.filter(t=>t.dueDate===tomorrow&&t.status!=="completed").length;
  const dueIn7 = tasks.filter(t=>t.dueDate>tomorrow&&t.dueDate<=in7&&t.status!=="completed").length;
  const dueUpto7 = tasks.filter(t=>t.dueDate>=T&&t.dueDate<=in7&&t.status!=="completed").length;
  const dueOver7 = tasks.filter(t=>t.dueDate>in7&&t.status!=="completed").length;

  const SERVICES = ["Accounting","GSTR-1","GSTR-3B","ITR (Non-Audit)","Audit","TDS"];
  const pending = tasks.filter(t=>t.status==="pending").length;
  const hold = tasks.filter(t=>t.status==="on-hold").length;
  const inProg = tasks.filter(t=>t.status==="in-progress").length;
  const done = tasks.filter(t=>t.status==="completed").length;
  const total = pending+hold+inProg+done;

  const byUser:Record<string,{total:number;completed:number}> = {};
  tasks.forEach(t=>{const u=byUser[t.assignedTo]??{total:0,completed:0};u.total++;if(t.status==="completed")u.completed++;byUser[t.assignedTo]=u;});
  const performers = Object.entries(byUser).map(([user,s])=>({user,...s,pct:s.total?Math.round(s.completed/s.total*100):0})).sort((a,b)=>b.pct-a.pct);

  return (
    <div className="pv-page">
      {/* Due strip */}
      <div className="pv-due-row">
        {[
          {num:dueToday,label:"Due",sub:"Today",color:"#f59e0b"},
          {num:dueTomorrow,label:"Due",sub:"Tomorrow",color:"#3b82f6"},
          {num:dueIn7,label:"Due",sub:"In 7 days",color:"#10b981"},
          {num:dueUpto7,label:"Due",sub:"Up to 7 days",color:"#f97316"},
          {num:dueOver7,label:"Due",sub:">7 days",color:"#8b5cf6"},
        ].map(d=>(
          <div key={d.sub} className="pv-due-box">
            <span className="pv-due-num" style={{color:d.color}}>{d.num}</span>
            <div><div className="pv-due-lbl">{d.label}</div><div className="pv-due-sub">{d.sub}</div></div>
          </div>
        ))}
      </div>

      <div className="pv-dash-layout">
        {/* Tasks section */}
        <div className="pv-dash-main">
          <div className="pv-section-card">
            <div className="pv-section-card-header">
              <strong>Tasks :</strong>
              <div style={{display:"flex",gap:8,marginLeft:"auto",alignItems:"center"}}>
                <button className="pv-pill-btn active" type="button">All Time ▾</button>
                <button className="pv-plus-btn" type="button">+</button>
              </div>
            </div>
            <table className="pv-task-matrix">
              <thead>
                <tr>
                  <th></th>
                  <th className="pv-col-pending">Pending</th>
                  <th className="pv-col-hold">Hold</th>
                  <th className="pv-col-inprog">In-Progress</th>
                  <th className="pv-col-done">Completed</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {SERVICES.map(svc=>{
                  const st = tasks.filter(t=>t.service.toLowerCase().includes(svc.split(" ")[0].toLowerCase()));
                  return (
                    <tr key={svc} className="pv-matrix-row" onClick={()=>navigate("tasks")}>
                      <td className="pv-matrix-svc">{svc}</td>
                      <td className="pv-matrix-n pending">{st.filter(t=>t.status==="pending").length||""}</td>
                      <td className="pv-matrix-n hold">{st.filter(t=>t.status==="on-hold").length||""}</td>
                      <td className="pv-matrix-n inprog">{st.filter(t=>t.status==="in-progress").length||""}</td>
                      <td className="pv-matrix-n done">{st.filter(t=>t.status==="completed").length||""}</td>
                      <td><div className="pv-ring-icon"/></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="pv-invoice-row">
              <span>Need to make invoice</span>
              <div><strong className="pv-inv-n">74</strong> <span className="pv-muted">Packages</span> &nbsp; <strong className="pv-inv-n pv-blue">568</strong> <span className="pv-muted">Tasks</span></div>
            </div>
          </div>

          <div className="pv-dash-bottom-row">
            {/* Best performer */}
            <div className="pv-section-card" style={{flex:1}}>
              <div className="pv-section-card-header">
                <strong>Best Performer</strong>
                <button className="pv-pill-btn active" style={{marginLeft:"auto"}} type="button">This Month ▾</button>
              </div>
              <table className="pv-performer-table">
                <thead><tr><th>USER</th><th>TOTAL</th><th>COMPLETED</th><th>%</th></tr></thead>
                <tbody>
                  {performers.map((p,i)=>(
                    <tr key={p.user}>
                      <td><div style={{display:"flex",alignItems:"center",gap:8}}><div className="pv-ua" style={{background:i===0?"#3b82f6":"#e2e8f0",color:i===0?"#fff":"#475569"}}>{p.user.slice(0,2).toUpperCase()}</div>{p.user}</div></td>
                      <td>{p.total}</td><td>{p.completed}</td>
                      <td><strong style={{color:p.pct>=80?"#10b981":p.pct>=50?"#3b82f6":"#f59e0b"}}>{p.pct}%</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* AI quick */}
            <div className="pv-ai-dash-card" onClick={()=>navigate("ai")}>
              <div className="pv-ai-dash-orb">✦</div>
              <div>
                <strong>AI Assistant</strong>
                <p>3 clients need follow-up · 2 deadlines this week · ₹2.4L unbilled</p>
              </div>
              <span>→</span>
            </div>
          </div>
        </div>

        {/* Right column */}
        <div className="pv-dash-right">
          {/* Task Summary */}
          <div className="pv-section-card">
            <div className="pv-section-card-header"><strong>Task Summary</strong></div>
            <div className="pv-donut-area">
              <svg viewBox="0 0 100 100" className="pv-donut-svg">
                <circle cx="50" cy="50" r="38" fill="none" stroke="#f1f5f9" strokeWidth="16"/>
                {[
                  {count:pending,color:"#f59e0b",offset:0},
                  {count:inProg,color:"#3b82f6",offset:pending},
                  {count:hold,color:"#94a3b8",offset:pending+inProg},
                  {count:done,color:"#10b981",offset:pending+inProg+hold},
                ].filter(s=>s.count>0).map((s,i)=>{
                  const circ=238; const pct=total?s.count/total:0;
                  const dash=circ*pct; const gap=circ*(1-pct);
                  const rotation = total ? -90 + (s.offset/total)*360 : -90;
                  return <circle key={i} cx="50" cy="50" r="38" fill="none" stroke={s.color} strokeWidth="16" strokeDasharray={`${dash} ${gap}`} strokeDashoffset="0" transform={`rotate(${rotation} 50 50)`}/>;
                })}
                <text x="50" y="47" textAnchor="middle" fontSize="16" fontWeight="900" fill="#0f172a">{total}</text>
                <text x="50" y="60" textAnchor="middle" fontSize="7" fill="#94a3b8">Total</text>
              </svg>
            </div>
            {[{label:"Pending",count:pending,color:"#f59e0b",icon:"○"},{label:"Hold",count:hold,color:"#94a3b8",icon:"⏸"},{label:"In-Progress",count:inProg,color:"#3b82f6",icon:"◎"},{label:"Completed",count:done,color:"#10b981",icon:"✓"}].map(s=>(
              <div key={s.label} className="pv-summary-line">
                <span style={{color:s.color}}>{s.icon}</span>
                <span className="pv-summary-lbl">{s.label}</span>
                <strong className="pv-summary-n" style={{color:s.color}}>{s.count}</strong>
              </div>
            ))}
            <div className="pv-need-complete">Need to complete tasks <strong>{tasks.filter(t=>t.status!=="completed").length}</strong></div>
          </div>

          {/* Holidays */}
          <div className="pv-section-card">
            <div className="pv-section-card-header"><strong>Upcoming Holidays</strong></div>
            {HOLIDAYS.map(h=><div key={h.name} className="pv-cal-row"><span>{h.name}</span><span className="pv-cal-date">{h.date}</span></div>)}
          </div>

          {/* Compliance */}
          <div className="pv-section-card">
            <div className="pv-section-card-header"><strong>Compliance Calendar</strong></div>
            {COMPLIANCE.map(c=><div key={c.name} className="pv-cal-row"><span>{c.name}</span><span className="pv-cal-date">{c.date}</span></div>)}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Tasks ───────────────────────────────────────────────────────────────────
const STATUS_COLOR:Record<string,string> = {pending:"#f59e0b","in-progress":"#3b82f6","on-hold":"#6b7280",completed:"#10b981",cancelled:"#ef4444"};
const STATUS_BG:Record<string,string> = {pending:"#fff8e1","in-progress":"#dbeafe","on-hold":"#f1f5f9",completed:"#dcfce7",cancelled:"#fee2e2"};

function TaskDetail({task,tasks,onUpdate,onBack}:{task:ClientTask;tasks:ClientTask[];onUpdate:(t:ClientTask[])=>void;onBack:()=>void}) {
  const [tab,setTab] = useState("Details");
  const [note,setNote] = useState("");
  const prog = checklistProgress(task);
  const TABS = ["Details","Timelog","To-Do","Documents","Task History"];

  const updateStatus = (status:string) => onUpdate(tasks.map(t=>t.id===task.id?{...t,status:status as any}:t));
  const toggleCheck = (ckId:string,done:boolean) => {
    const updated = {...task,checklist:task.checklist?.map(c=>c.id===ckId?{...c,done}:c)};
    onUpdate(tasks.map(t=>t.id===task.id?updated:t));
  };

  return (
    <div className="pv-page">
      <div className="pv-task-detail-hdr">
        <button className="pv-back-btn" onClick={onBack} type="button">← Tasks</button>
        <h2 className="pv-detail-title">{task.service}</h2>
        <div className="pv-detail-hdr-right">
          <select className="pv-status-pill" style={{background:STATUS_BG[task.status],color:STATUS_COLOR[task.status],borderColor:STATUS_COLOR[task.status]}} value={task.status} onChange={e=>updateStatus(e.target.value)}>
            <option value="pending">Pending</option><option value="in-progress">In-Progress</option><option value="on-hold">Hold</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option>
          </select>
          <button className="pv-verify-pill" type="button">Verify</button>
          <button className="pv-clock-pill" type="button">⏱</button>
        </div>
      </div>
      <div className="pv-detail-meta-row">
        <span><span className="pv-ml">Period:</span> <strong>{task.period??"—"}</strong></span>
        <span><span className="pv-ml">Client:</span> <strong className="pv-blue-link">{task.clientName}</strong></span>
      </div>

      <div className="pv-detail-tab-bar">
        {TABS.map(t=><button key={t} className={`pv-dtab ${tab===t?"active":""}`} onClick={()=>setTab(t)} type="button">{t}</button>)}
      </div>

      {tab==="Details"&&(
        <div className="pv-detail-body">
          <div className="pv-detail-left-card">
            {/* Progress timeline */}
            <div className="pv-timeline">
              <div className={`pv-tl-node done`}><div className="pv-tl-dot"/><span>Created</span><small>{(task.createdAt||"").slice(0,10)}</small></div>
              <div className={`pv-tl-line ${task.status!=="pending"?"done":""}`}/>
              <div className={`pv-tl-node ${task.status==="in-progress"||task.status==="completed"?"done":""}`}><div className="pv-tl-dot"/><span>In-Progress</span></div>
              <div className={`pv-tl-line ${task.status==="completed"?"done":""}`}/>
              <div className={`pv-tl-node ${task.status==="completed"?"done":""}`}><div className="pv-tl-dot"/><span>Completed</span></div>
            </div>
            <div className="pv-dfields">
              {[["Due Date",task.dueDate],["Created by","Akash"],["Invoice","Create Invoice"],["Is this task billable?","Yes"]].map(([k,v])=>(
                <div key={k} className="pv-dfield-row"><span className="pv-dfl">{k}</span><span className={k==="Invoice"?"pv-blue-link":"pv-dfv"}>{v}</span></div>
              ))}
              <div className="pv-dfield-row"><span className="pv-dfl">Users</span>
                <div style={{display:"flex",gap:4}}><div className="pv-ua">{task.assignedTo.slice(0,2).toUpperCase()}</div></div>
              </div>
              <div className="pv-dfield-row"><span className="pv-dfl">Tags</span><span className="pv-muted">There are no Tags</span></div>
              <div className="pv-dfield-row"><span className="pv-dfl">Custom Fields</span><span className="pv-muted">Number</span></div>
            </div>
          </div>

          <div className="pv-detail-right-col">
            {/* Subtasks / checklist */}
            <div className="pv-sub-card">
              <div className="pv-sub-header"><strong>Subtasks</strong><button className="pv-icon-link" type="button">+</button></div>
              {(task.checklist??[]).map(item=>(
                <div key={item.id} className={`pv-sub-item ${item.done?"done":""}`}>
                  <span className="pv-sub-drag">⠿</span>
                  <span className="pv-blue-link pv-sub-label">{item.label}</span>
                  <span className="pv-sub-status-pill" style={{background:item.done?STATUS_BG.completed:STATUS_BG.pending,color:item.done?STATUS_COLOR.completed:STATUS_COLOR.pending}}>
                    {item.done?"Completed":"Pending"}
                  </span>
                  <button className="pv-icon-link" type="button">⋮</button>
                </div>
              ))}
              {(task.checklist??[]).length===0&&<div className="pv-muted" style={{padding:"8px 0"}}>No subtasks.</div>}
            </div>

            {/* Notes */}
            <div className="pv-sub-card" style={{marginTop:14}}>
              <div className="pv-sub-header"><strong>Notes</strong></div>
              <textarea className="pv-notes-area" value={note} onChange={e=>setNote(e.target.value)} placeholder="Type your message..." rows={3}/>
            </div>
          </div>
        </div>
      )}

      {tab==="To-Do"&&(
        <div className="pv-todo-list">
          {(task.checklist??[]).map(item=>(
            <label key={item.id} className={`pv-todo-row ${item.done?"done":""}`}>
              <input type="checkbox" checked={item.done} onChange={e=>toggleCheck(item.id,e.target.checked)}/>
              <span>{item.label}</span>
              {item.done&&<span className="pv-done-check">✓</span>}
            </label>
          ))}
          {(task.checklist??[]).length===0&&<div className="pv-empty-state">No to-do items.</div>}
        </div>
      )}

      {(tab==="Timelog"||tab==="Documents"||tab==="Task History")&&(
        <div className="pv-empty-state" style={{padding:"40px 0"}}>No {tab.toLowerCase()} yet.</div>
      )}
    </div>
  );
}

function Tasks({tasks,onUpdate,notify}:{tasks:ClientTask[];onUpdate:(t:ClientTask[])=>void;notify:(m:string,t?:string)=>void}) {
  const [selected,setSelected] = useState<ClientTask|null>(null);
  const [showNew,setShowNew] = useState(false);
  const [svc,setSvc]=useState(""); const [clt,setClt]=useState(""); const [due,setDue]=useState(""); const [usr,setUsr]=useState("");
  const [filters,setFilters] = useState({service:"",status:"",user:"",client:""});

  if(selected) return <TaskDetail task={selected} tasks={tasks} onUpdate={t=>{onUpdate(t);setSelected(t.find(x=>x.id===selected.id)??null);}} onBack={()=>setSelected(null)}/>;

  const filtered = tasks.filter(t=>
    (!filters.service||t.service.toLowerCase().includes(filters.service.toLowerCase()))&&
    (!filters.status||t.status===filters.status)&&
    (!filters.user||t.assignedTo.toLowerCase().includes(filters.user.toLowerCase()))&&
    (!filters.client||t.clientName.toLowerCase().includes(filters.client.toLowerCase()))
  );

  const addTask = () => {
    if(!svc||!clt||!due) return;
    onUpdate([{id:uid(),clientId:uid(),clientName:clt,service:svc,status:"pending",priority:"normal",assignedTo:usr||"Unassigned",dueDate:due,billable:true,createdAt:new Date().toISOString()},...tasks]);
    setShowNew(false);setSvc("");setClt("");setDue("");setUsr("");
    notify("Task created","green");
  };

  return (
    <div className="pv-page">
      <div className="pv-page-header">
        <h2 className="pv-page-title">← Tasks</h2>
        <div style={{display:"flex",gap:8,marginLeft:"auto"}}>
          <button className="pv-export-btn" type="button">Export</button>
          <button className="pv-primary-btn" onClick={()=>setShowNew(true)} type="button">+ New</button>
        </div>
      </div>

      {/* Filters */}
      <div className="pv-filter-bar">
        <div className="pv-filter-grid">
          {[{label:"Service",key:"service",ph:"Select..."},{label:"Status",key:"status",ph:"Select..."},{label:"User",key:"user",ph:"Select..."},{label:"Client",key:"client",ph:"Select..."}].map(f=>(
            <div key={f.key} className="pv-filter-field">
              <label className="pv-filter-label">{f.label}</label>
              <input className="pv-filter-inp" value={(filters as any)[f.key]} onChange={e=>setFilters(p=>({...p,[f.key]:e.target.value}))} placeholder={f.ph}/>
            </div>
          ))}
        </div>
      </div>

      {showNew&&(
        <div className="pv-new-form">
          <strong style={{fontSize:14,display:"block",marginBottom:14}}>New Task</strong>
          <div className="pv-form-grid-2">
            <div className="pv-ff"><label>Service *</label><input value={svc} onChange={e=>setSvc(e.target.value)} placeholder="GSTR-3B (Monthly)"/></div>
            <div className="pv-ff"><label>Client *</label><input value={clt} onChange={e=>setClt(e.target.value)} placeholder="Client name"/></div>
            <div className="pv-ff"><label>Due Date *</label><input type="date" value={due} onChange={e=>setDue(e.target.value)}/></div>
            <div className="pv-ff"><label>Assign to</label><input value={usr} onChange={e=>setUsr(e.target.value)} placeholder="Team member"/></div>
          </div>
          <div style={{display:"flex",gap:8,marginTop:12}}>
            <button className="pv-primary-btn" onClick={addTask} type="button">Create</button>
            <button className="pv-export-btn" onClick={()=>setShowNew(false)} type="button">Cancel</button>
          </div>
        </div>
      )}

      {/* Task table */}
      <div className="pv-table-card">
        <table className="pv-tasks-table">
          <thead>
            <tr>
              <th><input type="checkbox"/></th>
              <th>DATE</th><th>TASK</th><th>CLIENT</th><th>DUE DATE</th>
              <th>COMPLETED ON</th><th>USERS</th><th>TAGS</th><th>STATUS</th><th>⚙</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(task=>{
              const overdue = task.dueDate<T&&task.status!=="completed";
              return (
                <tr key={task.id} className="pv-task-tr" onClick={()=>setSelected(task)}>
                  <td onClick={e=>e.stopPropagation()}><input type="checkbox"/></td>
                  <td className="pv-muted">{(task.createdAt||"").slice(0,10)}</td>
                  <td>
                    <div className="pv-task-name-cell">
                      <span className="pv-blue-link pv-task-name-link">{task.service}</span>
                      <span className="pv-muted">{task.period}</span>
                    </div>
                  </td>
                  <td>
                    <div className="pv-client-cell">
                      <span className="pv-blue-link">{task.clientName}</span>
                      {CLIENTS.find(c=>c.id===task.clientId)?.fileNo&&<span className="pv-file-badge">{CLIENTS.find(c=>c.id===task.clientId)?.fileNo}</span>}
                    </div>
                  </td>
                  <td className={overdue?"pv-overdue-text":""}>{task.dueDate}</td>
                  <td className="pv-muted">{task.status==="completed"?task.dueDate:"—"}</td>
                  <td><div className="pv-ua sm">{task.assignedTo.slice(0,2).toUpperCase()}</div></td>
                  <td>{(task.tags??[]).map(t=><span key={t} className="pv-tag-badge">{t}</span>)}</td>
                  <td>
                    <span className="pv-status-badge" style={{background:STATUS_BG[task.status],color:STATUS_COLOR[task.status]}}>
                      {task.status.replace(/-/g," ")}
                      {task.status==="completed"&&<span className="pv-green-dot">●</span>}
                    </span>
                  </td>
                  <td onClick={e=>e.stopPropagation()}><button className="pv-row-menu" type="button">⋮</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length===0&&<div className="pv-empty-state">No tasks found. Adjust your filters.</div>}
      </div>
    </div>
  );
}

// ─── Clients ─────────────────────────────────────────────────────────────────
function ClientDetail({client,onBack}:{client:SC;onBack:()=>void}) {
  const [tab,setTab]=useState("Details");
  const TABS=["Details","Services","Tasks","Documents","Ledger","Docs. In-Out Register","Passwords","Expenses","DSC","Quotations"];
  return (
    <div className="pv-page">
      <div className="pv-breadcrumb"><button className="pv-blue-link" onClick={onBack} type="button">Clients</button> <span>›</span> <span>Client Dashboard</span>
        <div style={{marginLeft:"auto",display:"flex",gap:8}}>
          <button className="pv-edit-btn" type="button">Edit</button>
          <button className="pv-del-btn" type="button">Delete</button>
        </div>
      </div>

      <div className="pv-client-detail-shell">
        {/* Left sidebar */}
        <div className="pv-client-sidebar">
          <div className="pv-client-logo-box">{initials(client.name)}</div>
          <div className="pv-client-sidebar-name">{client.name}</div>
          <div className="pv-client-sidebar-meta">File No.: {client.fileNo}</div>
          {client.group&&<div className="pv-client-sidebar-meta">Group: <span className="pv-blue-link">{client.group}</span></div>}
          <div className="pv-client-sidebar-section">Users</div>
          <div className="pv-ua" style={{marginTop:4}}>RK</div>
          <div className="pv-client-sidebar-section">Tags</div>
          <div style={{display:"flex",gap:4,flexWrap:"wrap",marginTop:4}}>
            {client.tags.map(t=><span key={t} className="pv-client-tag">{t}</span>)}
          </div>
        </div>

        {/* Right content */}
        <div className="pv-client-content">
          <div className="pv-detail-tab-bar" style={{marginBottom:0}}>
            {TABS.map(t=><button key={t} className={`pv-dtab ${tab===t?"active":""}`} onClick={()=>setTab(t)} type="button">{t}</button>)}
          </div>

          {/* Stats row */}
          <div className="pv-client-stats-row">
            <div className="pv-cstat-box"><strong>7</strong><span>Pending Tasks</span></div>
            <div className="pv-cstat-box"><strong>2</strong><span>Hold Tasks</span></div>
            <div className="pv-cstat-vdiv"/>
            <div className="pv-cstat-box"><strong>2</strong><span>In-Progress Tasks</span></div>
            <div className="pv-cstat-box"><strong>18</strong><span>Completed Tasks</span></div>
            <div className="pv-cstat-vdiv"/>
            <div className="pv-cstat-box"><strong>14</strong><span>Unbilled Tasks</span></div>
            <div className="pv-cstat-vdiv"/>
            <div className="pv-cstat-box"><strong style={{color:client.balanceType==="Dr"?"#ef4444":"#10b981"}}>{inr(client.balance)}</strong><span>Ledger Balance</span></div>
          </div>

          {tab==="Details"&&(
            <div className="pv-client-fields-grid">
              {[
                ["Status",<span style={{color:"#10b981",fontWeight:700}}>Active</span>],
                ["Type",client.type],
                ["Contact Person",client.contactPerson??"—"],
                ["Date of Birth",client.dob??"—"],
                ["PAN",client.pan??"—"],
                ["GSTIN",<span className="pv-blue-link">{client.gstin??"—"}</span>],
                ["Bank Details","Mr. ABC"],
                ["Contact No.","Director - 1234567890"],
                ["DIN No.",client.din??"—"],
                ["TAN No.",client.tan??"—"],
              ].map(([k,v])=>(
                <div key={k as string} className="pv-cfield"><span className="pv-cfl">{k}</span><span className="pv-cfv">{v as any}</span></div>
              ))}
              <div className="pv-cfield"><span className="pv-cfl">Mobile No.</span><span className="pv-cfv">{client.phone??"—"} {client.phone&&<span style={{color:"#10b981"}}>📞 💬</span>}</span></div>
              <div className="pv-cfield"><span className="pv-cfl">Email</span><span className="pv-cfv">{client.email??"—"}</span></div>
              <div className="pv-cfield"><span className="pv-cfl">Address</span><span className="pv-cfv">{client.address??"—"}</span></div>
              <div className="pv-cfield"><span className="pv-cfl">City</span><span className="pv-cfv pv-blue-link">{client.city??"—"}</span></div>
              <div className="pv-cfield"><span className="pv-cfl">Pincode</span><span className="pv-cfv">{client.pincode??"—"}</span></div>
              <div className="pv-cfield"><span className="pv-cfl">State</span><span className="pv-cfv">{client.state??"—"}</span></div>

              <div className="pv-cfield-full">
                <label className="pv-toggle-row"><span className="pv-toggle-track off"/><span>Create invoice automatically every month</span></label>
                <label className="pv-toggle-row"><span className="pv-toggle-track on"/><span>Allow Notifications</span></label>
                <label className="pv-toggle-row"><span className="pv-toggle-track on"/><span>Allow Login</span></label>
              </div>
              <div className="pv-cfield-full pv-created-by">Created by <span className="pv-ua" style={{display:"inline-flex",width:22,height:22,fontSize:8}}>P</span> <strong>Praveen</strong> on <span className="pv-muted">24-03-2023 10:50 AM</span></div>
            </div>
          )}
          {tab!=="Details"&&<div className="pv-empty-state" style={{padding:"40px 0"}}>No data yet for {tab}.</div>}
        </div>
      </div>
    </div>
  );
}

function Clients({clients,onAdd,notify}:{clients:SC[];onAdd:(c:SC)=>void;notify:(m:string,t?:string)=>void}) {
  const [search,setSearch]=useState("");
  const [selected,setSelected]=useState<SC|null>(null);
  const [adding,setAdding]=useState(false);

  if(adding) return (
    <div className="pv-page">
      <div className="pv-page-header"><h2 className="pv-page-title">New Client</h2></div>
      <div style={{maxWidth:700}}>
        <PartnerForm
          onSave={party=>{
            onAdd({...party as any,fileNo:`F${Math.floor(Math.random()*100)}`,tags:[],balance:0,balanceType:"Dr" as const,type:"Individual",status:"active" as const,autoInvoice:false,portalEnabled:false});
            setAdding(false);notify(`${party.name} added`,"green");
          }}
          onCancel={()=>setAdding(false)}
        />
      </div>
    </div>
  );
  if(selected) return <ClientDetail client={selected} onBack={()=>setSelected(null)}/>;

  const filtered = clients.filter(c=>!search||c.name.toLowerCase().includes(search.toLowerCase())||c.pan?.includes(search)||c.fileNo.includes(search));

  return (
    <div className="pv-page">
      <div className="pv-page-header">
        <h2 className="pv-page-title">Clients</h2>
        <div style={{display:"flex",gap:8,marginLeft:"auto"}}>
          <div className="pv-search-field"><span>⌕</span><input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search clients..."/></div>
          <button className="pv-export-btn" type="button">+ Import</button>
          <button className="pv-primary-btn" onClick={()=>setAdding(true)} type="button">+ New Client</button>
        </div>
      </div>

      <div className="pv-table-card">
        <table className="pv-tasks-table">
          <thead><tr><th>NAME</th><th>FILE NO.</th><th>TYPE</th><th>PAN</th><th>GSTIN</th><th>CITY</th><th>TAGS</th><th>BALANCE</th><th>STATUS</th></tr></thead>
          <tbody>
            {filtered.map(c=>(
              <tr key={c.id} className="pv-task-tr" onClick={()=>setSelected(c)}>
                <td><div style={{display:"flex",alignItems:"center",gap:10}}><div className="pv-ua">{initials(c.name)}</div><strong>{c.name}</strong></div></td>
                <td className="pv-muted">{c.fileNo}</td>
                <td className="pv-muted">{c.type}</td>
                <td>{c.pan??"—"}</td>
                <td className="pv-blue-link">{c.gstin??"—"}</td>
                <td className="pv-blue-link">{c.city??"—"}</td>
                <td>{c.tags.map(t=><span key={t} className="pv-tag-badge">{t}</span>)}</td>
                <td style={{color:c.balanceType==="Dr"?"#ef4444":"#10b981",fontWeight:700}}>{inr(c.balance)} {c.balanceType}</td>
                <td><span className="pv-status-badge" style={{background:"#dcfce7",color:"#15803d"}}>Active</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Communication Hub ───────────────────────────────────────────────────────
const COMM_TEMPLATES = [
  { id: "t1", name: "Compliance Reminder", channel: "WhatsApp", body: "Dear [Client], your [Return Type] for [Period] is due on [Date]. Kindly share the required documents at the earliest." },
  { id: "t2", name: "Document Request", channel: "WhatsApp", body: "Hi [Client], we need [Document] to proceed with your [Service]. Please share at your earliest convenience." },
  { id: "t3", name: "Payment Reminder", channel: "Email", body: "Dear [Client], your invoice #[Invoice No.] for [Amount] is overdue. Please arrange payment to avoid late fees." },
  { id: "t4", name: "DSC Expiry Alert", channel: "Email", body: "Dear [Client], your Digital Signature Certificate expires on [Date]. Let us know if you'd like us to initiate renewal." },
  { id: "t5", name: "Festival Greeting", channel: "WhatsApp", body: "Wishing you and your family a very Happy [Festival] from all of us at [Firm Name]!" },
];
const COMM_LOG = [
  { id: "l1", client: "A One Toys", channel: "WhatsApp", template: "Compliance Reminder", detail: "GSTR-3B due today", sentAt: "Today, 9:02 AM", status: "Delivered" },
  { id: "l2", client: "Mative Pvt Ltd", channel: "WhatsApp", template: "Compliance Reminder", detail: "GSTR-1 due tomorrow", sentAt: "Today, 9:02 AM", status: "Read" },
  { id: "l3", client: "Vaarahi RMC", channel: "Email", template: "Document Request", detail: "Tax Audit — bank statements pending", sentAt: "Yesterday, 4:20 PM", status: "Delivered" },
  { id: "l4", client: "Kumar Textiles", channel: "WhatsApp", template: "Compliance Reminder", detail: "Audit on hold — follow up", sentAt: "2 days ago", status: "Sent" },
  { id: "l5", client: "Prestige Builders", channel: "Email", template: "Payment Reminder", detail: "Invoice #INV-0231 overdue", sentAt: "3 days ago", status: "Read" },
];

function CommunicationHub({ notify }: { notify: (m: string, t?: string) => void }) {
  const [tab, setTab] = useState<"templates" | "log">("log");
  const [composeOpen, setComposeOpen] = useState(false);

  return (
    <div className="pv-page">
      <div className="pv-page-header">
        <h2 className="pv-page-title">Communication</h2>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button className="pv-pill-btn" onClick={() => setTab("log")} type="button" style={tab === "log" ? { background: "#eff6ff", borderColor: "#bfdbfe", color: "#1d4ed8" } : undefined}>Sent Log</button>
          <button className="pv-pill-btn" onClick={() => setTab("templates")} type="button" style={tab === "templates" ? { background: "#eff6ff", borderColor: "#bfdbfe", color: "#1d4ed8" } : undefined}>Templates</button>
          <button className="pv-primary-btn" onClick={() => setComposeOpen(true)} type="button">+ New Message</button>
        </div>
      </div>

      <div className="pv-due-row">
        <div className="pv-due-box"><div><div className="pv-due-num" style={{ color: "#2563eb" }}>{COMM_LOG.length}</div><div className="pv-due-lbl">Sent This Week</div></div></div>
        <div className="pv-due-box"><div><div className="pv-due-num" style={{ color: "#f59e0b" }}>3</div><div className="pv-due-lbl">Overdue Reminders Pending</div></div></div>
        <div className="pv-due-box"><div><div className="pv-due-num" style={{ color: "#10b981" }}>{COMM_LOG.filter(l => l.status === "Read").length}</div><div className="pv-due-lbl">Read</div></div></div>
      </div>

      {tab === "log" ? (
        <div className="pv-table-card">
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead><tr>{["Client", "Channel", "Template", "Detail", "Sent", "Status"].map(h => <th key={h} style={{ padding: "9px 12px", textAlign: "left", fontSize: 10, color: "#94a3b8", borderBottom: "1px solid #f1f5f9" }}>{h}</th>)}</tr></thead>
            <tbody>
              {COMM_LOG.map(l => (
                <tr key={l.id}>
                  <td style={{ padding: "9px 12px", borderBottom: "1px solid #f8fafc", fontWeight: 700, color: "#1e293b" }}>{l.client}</td>
                  <td style={{ padding: "9px 12px", borderBottom: "1px solid #f8fafc" }}><span className="pv-tag-badge">{l.channel}</span></td>
                  <td style={{ padding: "9px 12px", borderBottom: "1px solid #f8fafc" }}>{l.template}</td>
                  <td style={{ padding: "9px 12px", borderBottom: "1px solid #f8fafc", color: "#64748b" }}>{l.detail}</td>
                  <td style={{ padding: "9px 12px", borderBottom: "1px solid #f8fafc", color: "#94a3b8" }}>{l.sentAt}</td>
                  <td style={{ padding: "9px 12px", borderBottom: "1px solid #f8fafc" }}>
                    <span className="pv-status-badge" style={{ background: l.status === "Read" ? "#dcfce7" : l.status === "Delivered" ? "#dbeafe" : "#f1f5f9", color: l.status === "Read" ? "#15803d" : l.status === "Delivered" ? "#1d4ed8" : "#64748b" }}>{l.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {COMM_TEMPLATES.map(t => (
            <div key={t.id} className="pv-section-card">
              <div className="pv-section-card-header"><span className="pv-tag-badge">{t.channel}</span>{t.name}<button className="pv-icon-link" style={{ marginLeft: "auto", fontSize: 12 }} onClick={() => { setComposeOpen(true); }} type="button">Use →</button></div>
              <p className="pv-muted" style={{ fontSize: 11, lineHeight: 1.6, margin: 0 }}>{t.body}</p>
            </div>
          ))}
        </div>
      )}

      {composeOpen && (
        <div className="pv-modal-backdrop" onClick={() => setComposeOpen(false)}>
          <div className="pv-modal" onClick={e => e.stopPropagation()}>
            <div className="pv-modal-header"><strong>New message</strong><button className="pv-icon-link" onClick={() => setComposeOpen(false)} type="button">✕</button></div>
            <div className="pv-modal-body">
              <div className="pv-form-field"><label>Client</label><select className="pv-modal-input"><option>Select client…</option>{CLIENTS.map(c => <option key={c.id}>{c.name}</option>)}</select></div>
              <div className="pv-form-field"><label>Channel</label><select className="pv-modal-input"><option>WhatsApp</option><option>Email</option></select></div>
              <div className="pv-form-field"><label>Template</label><select className="pv-modal-input"><option>Blank message</option>{COMM_TEMPLATES.map(t => <option key={t.id}>{t.name}</option>)}</select></div>
              <div className="pv-form-field"><label>Message</label><textarea className="pv-modal-input" rows={4} placeholder="Type your message…" /></div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
                <button className="pv-export-btn" onClick={() => setComposeOpen(false)} type="button">Cancel</button>
                <button className="pv-primary-btn" onClick={() => { setComposeOpen(false); notify("Message sent", "green"); }} type="button">Send</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Documents & DSC register ────────────────────────────────────────────────
type DocRow = { id: string; client: string; docType: string; category: "Statutory" | "DSC" | "Financial" | "Registration"; uploadedOn: string; expiresOn?: string; status: "Valid" | "Expiring Soon" | "Expired" | "Missing" };
const DOCUMENTS: DocRow[] = [
  { id: "d1", client: "A One Toys", docType: "DSC — Class 3 (Director)", category: "DSC", uploadedOn: "12-Sep-2024", expiresOn: "05-Sep-2026", status: "Expiring Soon" },
  { id: "d2", client: "A One Toys", docType: "GST Registration Certificate", category: "Registration", uploadedOn: "01-Feb-2021", status: "Valid" },
  { id: "d3", client: "Numetra LTD", docType: "PAN Card (Company)", category: "Statutory", uploadedOn: "10-Jan-2020", status: "Valid" },
  { id: "d4", client: "Vaarahi RMC", docType: "DSC — Class 3 (Raghavendra)", category: "DSC", uploadedOn: "20-Jun-2025", expiresOn: "20-Jun-2027", status: "Valid" },
  { id: "d5", client: "Vaarahi RMC", docType: "Audit Report FY 2024-25", category: "Financial", uploadedOn: "18-Sep-2025", status: "Valid" },
  { id: "d6", client: "Kumar Textiles", docType: "Partnership Deed", category: "Registration", uploadedOn: "—", status: "Missing" },
  { id: "d7", client: "Prestige Builders", docType: "DSC — Class 3 (Vikram Nair)", category: "DSC", uploadedOn: "02-Aug-2024", expiresOn: "02-Aug-2026", status: "Expiring Soon" },
  { id: "d8", client: "Mative Pvt Ltd", docType: "MOA / AOA", category: "Registration", uploadedOn: "—", status: "Missing" },
];
const DOC_STATUS_COLOR: Record<DocRow["status"], { bg: string; fg: string }> = {
  Valid: { bg: "#dcfce7", fg: "#15803d" }, "Expiring Soon": { bg: "#fef3c7", fg: "#b45309" }, Expired: { bg: "#fee2e2", fg: "#b91c1c" }, Missing: { bg: "#f1f5f9", fg: "#64748b" },
};

function DocumentsRegister() {
  const [filter, setFilter] = useState<"all" | DocRow["category"]>("all");
  const rows = DOCUMENTS.filter(d => filter === "all" || d.category === filter);
  const expiringSoon = DOCUMENTS.filter(d => d.status === "Expiring Soon").length;
  const missing = DOCUMENTS.filter(d => d.status === "Missing").length;

  return (
    <div className="pv-page">
      <div className="pv-page-header">
        <h2 className="pv-page-title">Documents & DSC</h2>
        <button className="pv-primary-btn" style={{ marginLeft: "auto" }} type="button">+ Upload Document</button>
      </div>

      <div className="pv-due-row">
        <div className="pv-due-box"><div><div className="pv-due-num" style={{ color: "#2563eb" }}>{DOCUMENTS.length}</div><div className="pv-due-lbl">Total Documents</div></div></div>
        <div className="pv-due-box"><div><div className="pv-due-num" style={{ color: "#b45309" }}>{expiringSoon}</div><div className="pv-due-lbl">DSC Expiring &lt; 60 Days</div></div></div>
        <div className="pv-due-box"><div><div className="pv-due-num" style={{ color: "#64748b" }}>{missing}</div><div className="pv-due-lbl">Missing / Requested</div></div></div>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        {(["all", "Statutory", "DSC", "Financial", "Registration"] as const).map(f => (
          <button key={f} className="pv-pill-btn" onClick={() => setFilter(f)} type="button" style={filter === f ? { background: "#eff6ff", borderColor: "#bfdbfe", color: "#1d4ed8" } : undefined}>{f === "all" ? "All" : f}</button>
        ))}
      </div>

      <div className="pv-table-card">
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead><tr>{["Client", "Document", "Category", "Uploaded", "Expires", "Status", ""].map(h => <th key={h} style={{ padding: "9px 12px", textAlign: "left", fontSize: 10, color: "#94a3b8", borderBottom: "1px solid #f1f5f9" }}>{h}</th>)}</tr></thead>
          <tbody>
            {rows.map(d => {
              const c = DOC_STATUS_COLOR[d.status];
              return (
                <tr key={d.id}>
                  <td style={{ padding: "9px 12px", borderBottom: "1px solid #f8fafc", fontWeight: 700, color: "#1e293b" }}>{d.client}</td>
                  <td style={{ padding: "9px 12px", borderBottom: "1px solid #f8fafc" }}>{d.docType}</td>
                  <td style={{ padding: "9px 12px", borderBottom: "1px solid #f8fafc" }}><span className="pv-tag-badge">{d.category}</span></td>
                  <td style={{ padding: "9px 12px", borderBottom: "1px solid #f8fafc", color: "#94a3b8" }}>{d.uploadedOn}</td>
                  <td style={{ padding: "9px 12px", borderBottom: "1px solid #f8fafc", color: "#94a3b8" }}>{d.expiresOn ?? "—"}</td>
                  <td style={{ padding: "9px 12px", borderBottom: "1px solid #f8fafc" }}><span className="pv-status-badge" style={{ background: c.bg, color: c.fg }}>{d.status}</span></td>
                  <td style={{ padding: "9px 12px", borderBottom: "1px solid #f8fafc" }}>{d.status === "Missing" ? <span className="pv-blue-link">Request →</span> : <span className="pv-blue-link">View →</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Finance overview ────────────────────────────────────────────────────────
const AR_AGING: Array<{ client: string; balance: number; bucket: "0-30" | "31-60" | "61-90" | "90+" }> = [
  { client: "Vaarahi RMC", balance: 120000, bucket: "0-30" },
  { client: "A One Toys", balance: 64000, bucket: "31-60" },
  { client: "Kumar Textiles", balance: 32000, bucket: "61-90" },
  { client: "Numetra LTD", balance: 18000, bucket: "0-30" },
  { client: "Mative Pvt Ltd", balance: 5000, bucket: "90+" },
];

function FinanceOverview({ navigate }: { navigate: (s: CDSection) => void }) {
  const totalAR = AR_AGING.reduce((s, r) => s + r.balance, 0);
  const overdue60plus = AR_AGING.filter(r => r.bucket === "61-90" || r.bucket === "90+").reduce((s, r) => s + r.balance, 0);
  const unbilled = 240000;
  const collectedThisMonth = 186000;
  const buckets: Array<"0-30" | "31-60" | "61-90" | "90+"> = ["0-30", "31-60", "61-90", "90+"];

  const QUICK_LINKS: Array<{ key: CDSection; label: string; icon: string; desc: string }> = [
    { key: "invoices", label: "Invoices", icon: "🧾", desc: "Raise and track client invoices" },
    { key: "receipts", label: "Receipts", icon: "💵", desc: "Record payments received" },
    { key: "quotations", label: "Quotations", icon: "💬", desc: "Send service quotes" },
    { key: "expenses", label: "Expenses", icon: "💸", desc: "Firm operating expenses" },
  ];

  return (
    <div className="pv-page">
      <div className="pv-page-header"><h2 className="pv-page-title">Finance</h2></div>

      <div className="pv-due-row">
        <div className="pv-due-box"><div><div className="pv-due-num" style={{ color: "#ef4444" }}>{inr(totalAR)}</div><div className="pv-due-lbl">Total Receivable</div></div></div>
        <div className="pv-due-box"><div><div className="pv-due-num" style={{ color: "#b45309" }}>{inr(overdue60plus)}</div><div className="pv-due-lbl">Overdue &gt; 60 Days</div></div></div>
        <div className="pv-due-box"><div><div className="pv-due-num" style={{ color: "#10b981" }}>{inr(collectedThisMonth)}</div><div className="pv-due-lbl">Collected This Month</div></div></div>
        <div className="pv-due-box"><div><div className="pv-due-num" style={{ color: "#2563eb" }}>{inr(unbilled)}</div><div className="pv-due-lbl">Unbilled Work</div></div></div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 14 }}>
        {QUICK_LINKS.map(l => (
          <button key={l.key} className="pv-section-card" onClick={() => navigate(l.key)} type="button" style={{ textAlign: "left", cursor: "pointer", border: "1px solid #f1f5f9" }}>
            <div style={{ fontSize: 20, marginBottom: 6 }}>{l.icon}</div>
            <strong style={{ display: "block", fontSize: 13, color: "#1e293b" }}>{l.label}</strong>
            <span className="pv-muted">{l.desc}</span>
          </button>
        ))}
      </div>

      <div className="pv-section-card">
        <div className="pv-section-card-header">Accounts Receivable Ageing</div>
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          {buckets.map(b => {
            const sum = AR_AGING.filter(r => r.bucket === b).reduce((s, r) => s + r.balance, 0);
            return <div key={b} className="pv-tag-badge" style={{ fontSize: 10, padding: "5px 10px" }}>{b} days: <strong>{inr(sum)}</strong></div>;
          })}
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead><tr>{["Client", "Balance", "Ageing"].map(h => <th key={h} style={{ padding: "8px 10px", textAlign: "left", fontSize: 10, color: "#94a3b8", borderBottom: "1px solid #f1f5f9" }}>{h}</th>)}</tr></thead>
          <tbody>
            {AR_AGING.map(r => (
              <tr key={r.client}>
                <td style={{ padding: "8px 10px", borderBottom: "1px solid #f8fafc", fontWeight: 700, color: "#1e293b" }}>{r.client}</td>
                <td style={{ padding: "8px 10px", borderBottom: "1px solid #f8fafc", color: "#ef4444", fontWeight: 700 }}>{inr(r.balance)}</td>
                <td style={{ padding: "8px 10px", borderBottom: "1px solid #f8fafc" }}><span className="pv-status-badge" style={{ background: r.bucket === "0-30" ? "#dcfce7" : r.bucket === "31-60" ? "#fef3c7" : "#fee2e2", color: r.bucket === "0-30" ? "#15803d" : r.bucket === "31-60" ? "#b45309" : "#b91c1c" }}>{r.bucket} days</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Employees ───────────────────────────────────────────────────────────────
type StaffRow = { id: string; name: string; role: string; email: string; openTasks: number; overdueTasks: number; leaveBalance: number; status: "Active" | "On Leave" };
function buildStaff(tasks: ClientTask[]): StaffRow[] {
  const names = Array.from(new Set(tasks.map(t => t.assignedTo).filter(Boolean))) as string[];
  const roles: Record<string, string> = { Krithi: "Senior Associate", Ayush: "Associate", Rithika: "Article Assistant" };
  const leaveBalances: Record<string, number> = { Krithi: 8, Ayush: 12, Rithika: 15 };
  return names.map((name, i) => {
    const mine = tasks.filter(t => t.assignedTo === name);
    const open = mine.filter(t => t.status !== "completed").length;
    const overdue = mine.filter(t => t.status !== "completed" && t.dueDate < T).length;
    return { id: `s${i}`, name, role: roles[name] ?? "Team Member", email: `${name.toLowerCase()}@ledzerly.in`, openTasks: open, overdueTasks: overdue, leaveBalance: leaveBalances[name] ?? 10, status: "Active" };
  });
}

function EmployeeDirectory({ tasks }: { tasks: ClientTask[] }) {
  const staff = buildStaff(tasks);
  const maxOpen = Math.max(1, ...staff.map(s => s.openTasks));

  return (
    <div className="pv-page">
      <div className="pv-page-header">
        <h2 className="pv-page-title">Employees</h2>
        <button className="pv-primary-btn" style={{ marginLeft: "auto" }} type="button">+ Add Employee</button>
      </div>

      <div className="pv-due-row">
        <div className="pv-due-box"><div><div className="pv-due-num" style={{ color: "#2563eb" }}>{staff.length}</div><div className="pv-due-lbl">Active Staff</div></div></div>
        <div className="pv-due-box"><div><div className="pv-due-num" style={{ color: "#ef4444" }}>{staff.reduce((s, r) => s + r.overdueTasks, 0)}</div><div className="pv-due-lbl">Overdue Tasks (Team)</div></div></div>
        <div className="pv-due-box"><div><div className="pv-due-num" style={{ color: "#10b981" }}>{staff.reduce((s, r) => s + r.openTasks, 0)}</div><div className="pv-due-lbl">Open Tasks (Team)</div></div></div>
      </div>

      <div className="pv-section-card">
        <div className="pv-section-card-header">Workload by staff</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {staff.map(s => (
            <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div className="pv-ua sm">{initials(s.name)}</div>
              <div style={{ width: 90, fontSize: 12, fontWeight: 700, color: "#1e293b" }}>{s.name}</div>
              <div style={{ flex: 1, height: 8, background: "#f1f5f9", borderRadius: 99, overflow: "hidden" }}>
                <div style={{ width: `${(s.openTasks / maxOpen) * 100}%`, height: "100%", background: s.overdueTasks > 0 ? "#ef4444" : "#2563eb", borderRadius: 99 }} />
              </div>
              <div style={{ width: 70, fontSize: 11, color: "#64748b", textAlign: "right" }}>{s.openTasks} open</div>
            </div>
          ))}
        </div>
      </div>

      <div className="pv-table-card" style={{ marginTop: 14 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead><tr>{["Name", "Role", "Email", "Open Tasks", "Overdue", "Leave Balance", "Status"].map(h => <th key={h} style={{ padding: "9px 12px", textAlign: "left", fontSize: 10, color: "#94a3b8", borderBottom: "1px solid #f1f5f9" }}>{h}</th>)}</tr></thead>
          <tbody>
            {staff.map(s => (
              <tr key={s.id}>
                <td style={{ padding: "9px 12px", borderBottom: "1px solid #f8fafc", fontWeight: 700, color: "#1e293b" }}>{s.name}</td>
                <td style={{ padding: "9px 12px", borderBottom: "1px solid #f8fafc", color: "#64748b" }}>{s.role}</td>
                <td style={{ padding: "9px 12px", borderBottom: "1px solid #f8fafc", color: "#94a3b8" }}>{s.email}</td>
                <td style={{ padding: "9px 12px", borderBottom: "1px solid #f8fafc" }}>{s.openTasks}</td>
                <td style={{ padding: "9px 12px", borderBottom: "1px solid #f8fafc", color: s.overdueTasks > 0 ? "#ef4444" : "#94a3b8", fontWeight: s.overdueTasks > 0 ? 700 : 400 }}>{s.overdueTasks}</td>
                <td style={{ padding: "9px 12px", borderBottom: "1px solid #f8fafc" }}>{s.leaveBalance} days</td>
                <td style={{ padding: "9px 12px", borderBottom: "1px solid #f8fafc" }}><span className="pv-status-badge" style={{ background: "#dcfce7", color: "#15803d" }}>{s.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Stub ────────────────────────────────────────────────────────────────────
function Stub({label}:{label:string}) {
  return <div className="pv-page"><div className="pv-empty-state" style={{padding:"80px 0"}}><div style={{fontSize:40}}>🚧</div><strong style={{fontSize:16}}>{label}</strong><p>This section is coming soon.</p></div></div>;
}

// ─── Shell ───────────────────────────────────────────────────────────────────
interface CDProps { parties:Party[]; onAddParty:(p:Party)=>void; navigate:(v:string)=>void; notify:(m:string,t?:string)=>void; firmName?:string; userName?:string; }

export function ClientDeskView({parties,onAddParty,navigate,notify,firmName="CA Firm",userName="Raghavendra K."}:CDProps) {
  const [section,setSection]=useState<CDSection>("dashboard");
  const [tasks,setTasks]=useState<ClientTask[]>(TASKS);
  const [clients,setClients]=useState<SC[]>(CLIENTS);

  return (
    <div className="pv-shell">
      {/* Sidebar */}
      <aside className="pv-sidebar">
        <div className="pv-sidebar-top">
          <div className="pv-sidebar-brand">
            <img src="/product-icons/clientdesk.png" alt="ClientDesk" className="pv-sidebar-product-icon" />
            <div className="pv-sidebar-wordmark">
              <span className="pv-wm-first">Client</span><span className="pv-wm-last">Desk</span>
            </div>
          </div>
          <div className="pv-sidebar-search"><span>⌕</span><input placeholder="Search Clients"/></div>
        </div>
        <nav className="pv-sidebar-nav">
          {NAV_GROUPS.map((group,gi)=>(
            <div key={gi} className="pv-nav-group">
              {group.items.map(item=>(
                <button key={item.key} className={`pv-nav-item ${section===item.key?"active":""}`} onClick={()=>setSection(item.key)} type="button">
                  <span className="pv-nav-icon">{item.icon}</span>
                  <span className="pv-nav-label">{item.label}</span>
                  {item.badge&&<span className="pv-nav-new-badge">{item.badge}</span>}
                </button>
              ))}
            </div>
          ))}
          <div className="pv-nav-group">
            <button className={`pv-nav-item ${section==="ai"?"active":""}`} onClick={()=>setSection("ai")} type="button">
              <span className="pv-nav-icon">✦</span>
              <span className="pv-nav-label">AI Assistant</span>
              <span className="pv-nav-ai-badge">New</span>
            </button>
            <button className="pv-nav-item pv-nav-send" onClick={()=>notify("Send notifications","blue")} type="button">
              <span className="pv-nav-icon">📢</span>
              <span className="pv-nav-label">Send Notifications</span>
            </button>
          </div>
        </nav>
        <div className="pv-sidebar-footer-links">
          <button className="pv-nav-item" onClick={()=>notify("Settings","blue")} type="button"><span className="pv-nav-icon">⚙</span><span className="pv-nav-label">Settings</span></button>
          <button className="pv-nav-item pv-nav-audit" onClick={()=>navigate("audit")} type="button"><span className="pv-nav-icon">🔍</span><span className="pv-nav-label">AuditFlow ↗</span></button>
          <button className="pv-nav-item" onClick={()=>navigate("suite")} type="button"><span className="pv-nav-icon">⬡</span><span className="pv-nav-label">Ledzerly Suite ↗</span></button>
        </div>
      </aside>

      {/* Main */}
      <div className="pv-main">
        <div className="pv-topbar">
          <button className="suite-home-trigger" onClick={()=>navigate("suite")} type="button" aria-label="Open Ledzerly product suite" title="Back to Ledzerly Product Suite"><span/><span/><span/><span/></button>
          <div className="pv-topbar-search"><span>⌕</span><input placeholder="Search Clients"/></div>
          <div className="pv-topbar-right">
            <span className="pv-firm-name">{firmName}</span>
            <button className="pv-topbar-btn pv-whats-new" type="button">Whats new ●</button>
            <button className="pv-topbar-icon" type="button">⏱</button>
            <button className="pv-topbar-icon pv-notif-btn" type="button">🔔<span className="pv-notif-badge">28</span></button>
            <button className="pv-topbar-icon" type="button">?</button>
            <div className="pv-topbar-avatar">{initials(userName)}</div>
          </div>
        </div>

        <div className="pv-content">
          {section==="dashboard"&&<Dashboard tasks={tasks} navigate={setSection}/>}
          {section==="tasks"&&<Tasks tasks={tasks} onUpdate={setTasks} notify={notify}/>}
          {section==="clients"&&<Clients clients={clients} onAdd={c=>setClients(p=>[c,...p])} notify={notify}/>}
          {section==="ai"&&<AIPanel notify={notify}/>}
          {section==="communication"&&<CommunicationHub notify={notify}/>}
          {section==="documents"&&<DocumentsRegister/>}
          {section==="finance"&&<FinanceOverview navigate={setSection}/>}
          {section==="employees"&&<EmployeeDirectory tasks={tasks}/>}
          {(["todo","packages","services","invoices","receipts","quotations","expenses","attendance","reports","users"] as CDSection[]).includes(section)&&<Stub label={section.charAt(0).toUpperCase()+section.slice(1)}/>}
        </div>
      </div>
    </div>
  );
}
